import React from "react";
import { Anchor } from "lucide-react";
/**
 * Reusable Rolling River tip letter renderer.
 * Preserves the exact 2025 format for Group, CIT, Bus, and AM/PM letters.
 *
 * Props:
 *   title            - subtitle after "Summer 20XX Tip Policy-"
 *   settings         - camp settings object from /api/settings
 *   audience         - "group" | "cit" | "bus" | "ampm"
 *   staffTable       - array of { role, name }  -- rendered as Group Staff table
 *   rateRows         - array of { role, full, half }  -- pricing rows
 *   extraNote        - optional note above the "How to Submit" section
 *   distributionDay  - override for last day text
 */
export default function TipLetter({
  title,
  settings,
  audience = "group",
  staffTable = [],
  rateRows = [],
  extraNote = null,
  showDeadlines = true,
}) {
  if (!settings) return null;

  const {
    camp_name,
    camp_est,
    season_year,
    phone,
    signature_1,
    signature_2,
    signoff,
    last_day_of_camp,
    half_season_1_deadline,
    full_season_deadline,
  } = settings;

  return (
    <article
      className="letter-page"
      data-testid={`tip-letter-${audience}`}
    >
      {/* Header logo mark */}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="rr-mark">
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#0B2545] text-[#FDF6EC]">
            <Anchor className="w-4 h-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="rr-title">{camp_name?.split(" ").slice(0, 2).join(" ") || "Rolling River"}</span>
            <span className="text-[7pt] tracking-widest font-mono-camp text-[#13315C]/70 uppercase">
              Day Camp
            </span>
          </div>
          <div className="rr-est">
            <span>EST.</span>
            <span>{camp_est || "1992"}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono-camp text-[7.5pt] tracking-widest uppercase text-[#13315C]/70">
            Summer {season_year} Tip Policy
          </div>
          <div className="font-display text-[13pt] font-semibold text-[#0B2545] leading-tight mt-0.5">
            {title}
          </div>
        </div>
      </div>

      <h1 className="letter-h1">Summer {season_year} Tip Policy - {title}</h1>

      <div className="letter-body">
        <p>
          Thank you for choosing {camp_name} and for trusting our dedicated
          staff with your child this summer! Our counselors work hard each day
          to create a safe, fun, and memorable experience for every camper.
          Over the years, many families have asked about gratuity guidelines to
          show appreciation for their child&apos;s counselors. While tipping is at
          your discretion, below you&apos;ll find suggested amounts along with the
          names and roles of your child&apos;s {audience === "bus" ? "Bus Staff" : audience === "ampm" ? "AM/PM Care Staff" : audience === "cit" ? "CIT Supervisors" : "Group Staff"}:
        </p>

        {/* Rates table */}
        {rateRows.length > 0 && (
          <table className="tip-table" data-testid="rates-table">
            <thead>
              <tr>
                <th>{audience === "bus" ? "Bus Role" : audience === "ampm" ? "Counselor Role" : audience === "cit" ? "CIT Role" : "Group Counselor Role"}</th>
                <th>Full Season Amount</th>
                <th>Half Season Amount</th>
              </tr>
            </thead>
            <tbody>
              {rateRows.map((r, i) => (
                <tr key={i}>
                  <td>{r.role}</td>
                  <td>{r.full}</td>
                  <td>{r.half}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Staff table */}
        {staffTable.length > 0 && (
          <>
            <h3 className="letter-h3">
              {audience === "bus"
                ? `Bus #${title.replace(/[^0-9]/g, "")} Staff`
                : audience === "ampm"
                ? "Group Counselor Names"
                : audience === "cit"
                ? "CIT Supervisors"
                : `${title} Staff`}
            </h3>
            <table className="staff-table" data-testid="staff-table">
              <tbody>
                {staffTable.map((row, i) => (
                  <tr key={i}>
                    <td>{row.role}</td>
                    <td data-testid={`staff-name-${i}`}>{row.name || <span className="italic text-[#8a7a58]">Not assigned</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <p className="mt-3">
          <em>
            Instructional Swim Staff, Office Staff &amp; others can be tipped
            at your discretion.
          </em>
        </p>

        {extraNote && <p>{extraNote}</p>}

        <h3 className="letter-h3">How to Submit Gratuities</h3>
        <p>
          To help prevent misplaced envelopes and ensure accurate distribution,
          please do not hand gratuities directly to counselors. Instead, follow
          these steps:
        </p>
        <ul>
          <li>
            Use the provided white &ldquo;Rolling River Tip Envelope&rdquo; for
            each individual staff member.
          </li>
          <li>Clearly write the counselor&apos;s name on the envelope.</li>
          <li>Complete the brief form on the envelope.</li>
          <li>Enclose the exact amount of cash or a check made out to the counselor.</li>
          <li>Place all completed white envelopes into the provided tan envelope and seal securely.</li>
          <li>Send the sealed tan envelope to camp with your child via their Bus Counselor or drop it off in the Main Office.</li>
          <li>All tips will be collected and distributed in one labeled envelope per counselor on the last day of camp, {last_day_of_camp}.</li>
        </ul>

        {showDeadlines && (
          <>
            <h3 className="letter-h3">Important Deadlines</h3>
            <ul>
              <li>
                <strong>Half Season 1 Campers:</strong> Please submit gratuities by {half_season_1_deadline}
              </li>
              <li>
                <strong>Full Season Campers:</strong> Please submit gratuities by {full_season_deadline}
              </li>
            </ul>
          </>
        )}

        <p className="mt-4">
          We appreciate your generosity and support in recognizing our
          exceptional staff. If you have any questions about the process, feel
          free to contact us at {phone}.
        </p>

        <div className="mt-6">
          <div className="font-display italic text-[10.5pt] text-[#0B2545]">
            {signoff}
          </div>
          <div className="mt-2 text-[9pt]">
            <div>{signature_1}</div>
            <div>{signature_2}</div>
          </div>
        </div>
      </div>
    </article>
  );
}
